import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { AnomalyDetail as AnomalyDetailType } from '../types';
import SeverityBadge from '../components/SeverityBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const snoozeDurations = [
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4 },
  { label: '24 hours', hours: 24 },
  { label: '7 days', hours: 168 },
];

const AnomalyDetail: React.FC = () => {
  const { accountId, region, anomalyId } = useParams<{
    accountId: string;
    region: string;
    anomalyId: string;
  }>();
  const [anomaly, setAnomaly] = useState<AnomalyDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acking, setAcking] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId || !region || !anomalyId) return;
    api
      .get<AnomalyDetailType>(`/anomalies/${accountId}/${region}/${anomalyId}`)
      .then(setAnomaly)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [accountId, region, anomalyId]);

  const handleAck = async (snoozeUntil?: string) => {
    if (!accountId || !region || !anomalyId) return;
    setAcking(true);
    setAckError(null);
    try {
      const body: Record<string, string> = {};
      if (snoozeUntil) body.snoozeUntil = snoozeUntil;
      await api.post(`/anomalies/${accountId}/${region}/${anomalyId}/ack`, body);
      const updated = await api.get<AnomalyDetailType>(
        `/anomalies/${accountId}/${region}/${anomalyId}`
      );
      setAnomaly(updated);
    } catch (err: any) {
      setAckError(err.message);
    } finally {
      setAcking(false);
    }
  };

  const handleSnooze = (hours: number) => {
    const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    handleAck(until);
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!anomaly) return <ErrorMessage message="Anomaly not found." />;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/anomalies" className="text-sm text-slate-500 hover:text-brand-700">← Back to anomalies</Link>
        <h1 className="mt-2">Anomaly Detail</h1>
      </div>

      <div className="card-padded">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <Field label="App" value={anomaly.appId} />
          <Field label="Stack" value={anomaly.stackId} />
          <Field label="Account" value={<code className="font-mono">{anomaly.accountId}</code>} />
          <Field label="Region" value={anomaly.region} />
          <Field label="Priority" value={<SeverityBadge priority={anomaly.priority} />} />
          <Field label="Reported" value={new Date(anomaly.reportedAt).toLocaleString()} />
          <Field label="Log Group" value={<code className="font-mono text-xs break-all">{anomaly.logGroupName}</code>} fullWidth />
        </dl>
      </div>

      {anomaly.insight && (
        <div className="card">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
            <svg className="h-4 w-4 text-brand-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
              <path d="M10 2a1 1 0 0 1 1 1v1.06a6 6 0 0 1 4.94 4.94H17a1 1 0 1 1 0 2h-1.06A6 6 0 0 1 11 15.94V17a1 1 0 1 1-2 0v-1.06A6 6 0 0 1 4.06 11H3a1 1 0 1 1 0-2h1.06A6 6 0 0 1 9 4.06V3a1 1 0 0 1 1-1Z" />
            </svg>
            <h3>AI Triage Insight</h3>
          </div>
          <div className="p-5 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
            {anomaly.insight}
          </div>
        </div>
      )}

      <div className="card-padded">
        <h3 className="mb-3">Acknowledgement</h3>
        {anomaly.ackState ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
            <div>Acknowledged by <strong>{anomaly.ackState.ackedBy}</strong></div>
            <div className="text-emerald-700">At {new Date(anomaly.ackState.ackedAt).toLocaleString()}</div>
            {anomaly.ackState.snoozeUntil && (
              <div className="text-emerald-700">
                Snoozed until {new Date(anomaly.ackState.snoozeUntil).toLocaleString()}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => handleAck()} disabled={acking} className="btn-primary">
              {acking ? 'Acknowledging…' : 'Acknowledge'}
            </button>
            <span className="text-xs text-slate-500 px-1">or snooze for</span>
            {snoozeDurations.map((d) => (
              <button
                key={d.hours}
                onClick={() => handleSnooze(d.hours)}
                disabled={acking}
                className="btn"
              >
                {d.label}
              </button>
            ))}
          </div>
        )}
        {ackError && <div className="mt-3"><ErrorMessage message={ackError} /></div>}
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode; fullWidth?: boolean }> = ({
  label,
  value,
  fullWidth = false,
}) => (
  <div className={fullWidth ? 'sm:col-span-2' : ''}>
    <dt className="label">{label}</dt>
    <dd className="text-sm text-slate-900">{value}</dd>
  </div>
);

export default AnomalyDetail;
